/**
 * Single source of truth for the backend origin + runtime shell detection.
 *
 * On the **web build** `VITE_API_BASE` is unset, so everything resolves to the
 * current page origin — relative `/api` paths and a same-host WebSocket — which
 * is byte-for-byte the historical same-origin behaviour.
 *
 * The **Tauri desktop build** sets `VITE_API_BASE` to an absolute origin
 * (e.g. `https://lexicanon.com`) because the webview is served from a `tauri://`
 * protocol and cannot reach the backend through relative URLs or
 * `window.location.host`.
 */

const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : ""

/** Absolute backend origin, or "" on the web build (⇒ same-origin relative URLs). */
export const API_ORIGIN: string = import.meta.env.VITE_API_BASE ?? import.meta.env.VITE_API_URL ?? fallbackOrigin

/** Join an `/api/...` path onto the configured origin. Returns the path unchanged on web. */
export function apiUrl(path: string): string {
  return `${API_ORIGIN}${path}`
}

/** `ws://` or `wss://` origin matching `API_ORIGIN` (or the current host on web). */
export function wsOrigin(): string {
  if (API_ORIGIN) return API_ORIGIN.replace(/^http/, "ws")
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  return `${protocol}//${window.location.host}`
}

/** True when running inside the Tauri desktop shell (Tauri v2 injects `__TAURI_INTERNALS__`). */
export const IS_TAURI: boolean = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window

/**
 * Send the desktop shell back to its bundled workspace picker.
 *
 * The desktop app is a thin shell: a tiny bundled launcher
 * (`desktop/launcher/index.html`) asks which workspace to open, remembers it, and
 * redirects the webview to `https://<workspace>.lexicanon.com`. Once redirected the
 * webview has left the launcher, so there is no in-page way to change the workspace
 * again. Navigating to the launcher's bundled-asset URL with `?switch` re-opens its
 * picker — the launcher clears the saved workspace whenever `?switch` is present.
 *
 * Tauri v2 serves bundled assets from a platform-specific origin (it does not use a
 * localhost HTTP server): `tauri://localhost` on macOS/Linux, `http://tauri.localhost`
 * on Windows (WebView2 requires the http form). No-op outside the desktop shell.
 */
export function openWorkspaceSwitcher(): void {
  if (!IS_TAURI) return
  const isWindows = typeof navigator !== "undefined" && navigator.userAgent.includes("Windows")
  const launcherOrigin = isWindows ? "http://tauri.localhost" : "tauri://localhost"
  window.location.href = `${launcherOrigin}/index.html?switch`
}

/**
 * True when running as an installed PWA (launched from the home screen /
 * standalone window) rather than a normal browser tab. `display-mode: standalone`
 * covers Android + desktop installs; the non-standard `navigator.standalone`
 * covers older iOS Safari home-screen apps.
 */
export const IS_STANDALONE_PWA: boolean = (() => {
  if (typeof window === "undefined") return false
  const byDisplayMode = window.matchMedia?.("(display-mode: standalone)").matches === true
  const nav: Navigator & { standalone?: boolean } = window.navigator
  return byDisplayMode || nav.standalone === true
})()

/**
 * Personal-device shells — the desktop app or an installed mobile/PWA. These are
 * single-user, trusted devices, so they get long-lived ("remember me") sessions
 * by default; a plain browser does not. Used by the login flow.
 */
export const IS_PERSONAL_DEVICE: boolean = IS_TAURI || IS_STANDALONE_PWA
